/**
 * CORE-BQ Closure Slice B — reading and writing operational-integrity results.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONE RULE THIS MODULE EXISTS TO ENFORCE
 * ═══════════════════════════════════════════════════════════════════════════
 * **No rows must never mean healthy.**
 *
 * That sounds obvious and is the single easiest thing to get wrong here,
 * because the natural implementation of an integrity page — select today's
 * violations, show a green tick when the list is empty — is exactly the bug.
 * An empty list is produced by four different situations:
 *
 *   the audit ran, covered everything, and found nothing   → healthy
 *   the audit ran but did not finish looking               → NOT healthy
 *   the audit never ran for this shop                      → NOT healthy
 *   the audit ran and the write failed                     → NOT healthy
 *
 * Only the first is good news, and three of the four are indistinguishable
 * downstream unless something upstream keeps them apart. So health is derived
 * from the RUN, never from the absence of violations, and `deriveIntegrityState`
 * is the only place that derivation happens.
 *
 * The database is the other half: `integrity_audit_runs` has a CHECK making an
 * inconsistent run unrepresentable, `record_integrity_audit` derives the verdict
 * instead of accepting one, and it writes the run and its findings in one
 * transaction so a failed write leaves no run at all — which this module then
 * reports as "never audited" rather than as a clean board.
 */
import {
  INVARIANT_RULES, INVARIANT_RULE_IDS, enforcementOf, type InvariantRuleId,
} from './invariantCoverage';
import type { ScheduleViolation, ScheduleInvariantsResult } from './invariants';

/**
 * How long a result may stand before it stops describing "now".
 *
 * A shop's board changes continuously, so yesterday's clean audit is evidence
 * about yesterday. One working day is deliberately generous — the point is not
 * to police freshness, it is to stop a months-old PASS presenting itself as the
 * current state.
 */
export const INTEGRITY_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/** A persisted run, as the surface reads it back. */
export interface IntegrityRunRow {
  id: string;
  verdict: 'PASS' | 'FAIL' | 'INCOMPLETE';
  coverage_complete: boolean;
  violation_count: number;
  rules_checked: number;
  rules_unevaluated: number;
  checked_at: string;
  entry_point: string;
  source: string;
}

/** A persisted violation, as the surface reads it back. */
export interface IntegrityViolationRow {
  id: string;
  rule: string;
  severity: 'error' | 'warning';
  message: string;
  record_kind: string | null;
  record_id: string | null;
  fields: string[] | null;
  details: unknown;
  observed_at: string;
}

/**
 * What the shop is entitled to be told.
 *
 * `HEALTHY` is the only value that asserts anything good, and it is reachable
 * from exactly one combination: a run exists, it finished looking, it found
 * nothing, and it is recent enough to be about now.
 */
export type IntegrityState =
  /** No audit has ever been recorded for this shop. */
  | 'NEVER_AUDITED'
  /** The most recent audit did not finish looking. Silence proves nothing. */
  | 'AUDIT_INCOMPLETE'
  /** The most recent complete audit found contradictions. */
  | 'VIOLATIONS_FOUND'
  /** The last complete clean audit is too old to describe the board now. */
  | 'STALE'
  /** A complete, recent audit found nothing. */
  | 'HEALTHY';

export interface IntegrityStatus {
  state: IntegrityState;
  /** True whenever the newest run is older than the window, whatever it said. */
  isStale: boolean;
  checkedAt: Date | null;
  run: IntegrityRunRow | null;
  /** One plain sentence, safe to render directly. */
  headline: string;
}

/**
 * The single derivation. Ordering is deliberate:
 *
 * Staleness DEMOTES the good state and never suppresses a bad one. A stale
 * audit that found violations still reports them — they are the most actionable
 * thing known, and hiding a real finding behind "this is old" would trade one
 * dishonesty for another. A stale audit that found nothing cannot say healthy,
 * because it is not evidence about now.
 */
export function deriveIntegrityState(
  run: IntegrityRunRow | null | undefined,
  now: Date = new Date(),
  staleAfterMs: number = INTEGRITY_STALE_AFTER_MS,
): IntegrityStatus {
  if (!run) {
    return {
      state: 'NEVER_AUDITED', isStale: false, checkedAt: null, run: null,
      headline: 'No integrity audit has been recorded for this shop yet.',
    };
  }

  const checkedAt = new Date(run.checked_at);
  const age = now.getTime() - checkedAt.getTime();
  const isStale = Number.isFinite(age) && age > staleAfterMs;

  if (run.verdict === 'FAIL') {
    return {
      state: 'VIOLATIONS_FOUND', isStale, checkedAt, run,
      headline: `${run.violation_count} contradiction${run.violation_count === 1 ? '' : 's'} found in the last audit.`,
    };
  }

  if (run.verdict === 'INCOMPLETE' || !run.coverage_complete) {
    // Belt and braces: the database CHECK already makes PASS-with-incomplete
    // -coverage unrepresentable, and this does not rely on that holding.
    return {
      state: 'AUDIT_INCOMPLETE', isStale, checkedAt, run,
      headline: `The last audit did not finish looking — ${run.rules_unevaluated} rule${run.rules_unevaluated === 1 ? ' was' : 's were'} not evaluated. Nothing was found, and that is not the same as nothing being wrong.`,
    };
  }

  if (isStale) {
    return {
      state: 'STALE', isStale, checkedAt, run,
      headline: 'The last complete audit found nothing, but it is too old to describe the board as it stands now.',
    };
  }

  return {
    state: 'HEALTHY', isStale: false, checkedAt, run,
    headline: `All ${run.rules_checked} checked invariants held at the last audit.`,
  };
}

/** Only one state is good news. Kept as a function so no caller re-invents it. */
export function isReassuring(status: IntegrityStatus): boolean {
  return status.state === 'HEALTHY';
}

/* ═══════════════════════════ the write side ═══════════════════════════════ */

/** A violation flattened for the recording RPC. */
export interface PersistableViolation {
  rule: string;
  severity: 'error' | 'warning';
  message: string;
  record_kind: 'ticket' | 'technician' | 'bay' | null;
  record_id: string | null;
  fields: string[];
  details: unknown;
}

/**
 * WHICH RECORD. The violation shape carries up to three identifiers; the one
 * present is the record the finding is about. Ticket first because it is the
 * subject of most rules, then technician, then bay.
 */
function recordOf(v: ScheduleViolation): Pick<PersistableViolation, 'record_kind' | 'record_id'> {
  if (v.ticketId) return { record_kind: 'ticket', record_id: v.ticketId };
  if (v.techId) return { record_kind: 'technician', record_id: v.techId };
  if (v.bayId) return { record_kind: 'bay', record_id: v.bayId };
  return { record_kind: null, record_id: null };
}

/**
 * WHICH FIELD, at the fidelity the engine actually has.
 *
 * A violation does not name one offending column, and for most of these rules
 * there is not one to name: an overlap is a relation between two rows, a
 * bay-sync failure is a disagreement between two tables. What the registry does
 * declare — since Slice A — is the set of columns each rule reads. Those are
 * the fields the finding is about, and reporting the set is truthful where
 * inventing a single culprit would not be.
 *
 * Qualified by dataset, because `id` means different things on different planes.
 */
export function fieldsForRule(rule: string): string[] {
  const def = (INVARIANT_RULES as Record<string, { requires?: Record<string, readonly string[]> }>)[rule];
  const requires = def?.requires;
  if (!requires) return [];
  return Object.entries(requires)
    .flatMap(([dataset, columns]) => (columns ?? []).map((c) => `${dataset}.${c}`))
    .sort();
}

export function toPersistableViolations(
  violations: readonly ScheduleViolation[],
): PersistableViolation[] {
  return violations.map((v) => ({
    rule: v.rule,
    severity: v.severity === 'warning' ? 'warning' : 'error',
    message: v.message,
    ...recordOf(v),
    fields: fieldsForRule(v.rule),
    details: v.details ?? null,
  }));
}

/** What the recorder needs from a completed audit. */
export interface AuditRecordInput {
  shopId: string;
  entryPoint: 'schedule' | 'lifecycle';
  source: string;
  result: ScheduleInvariantsResult;
  checkedAt?: Date;
}

export interface AuditRecordPayload {
  p_shop_id: string;
  p_entry_point: string;
  p_source: string;
  p_coverage_complete: boolean;
  p_violations: PersistableViolation[];
  p_coverage: unknown;
  p_rules_checked: number;
  p_rules_unevaluated: number;
  p_checked_at: string;
}

/**
 * Build the RPC arguments from a real audit result.
 *
 * The verdict is deliberately NOT sent. The database derives it from the
 * violations and the coverage, so there is exactly one implementation of that
 * rule and no way for a caller — mistaken or otherwise — to record a verdict
 * its own findings contradict.
 */
export function buildAuditRecordPayload(input: AuditRecordInput): AuditRecordPayload {
  const { coverage, violations } = input.result;
  const checks = coverage?.checks ?? [];
  const checked = checks.filter((c) => c.status === 'CHECKED' || c.status === 'NOT_APPLICABLE');
  return {
    p_shop_id: input.shopId,
    p_entry_point: input.entryPoint,
    p_source: input.source,
    p_coverage_complete: !!coverage?.complete,
    p_violations: toPersistableViolations(violations ?? []),
    p_coverage: coverage ?? null,
    p_rules_checked: checked.length,
    p_rules_unevaluated: checks.length - checked.length,
    p_checked_at: (input.checkedAt ?? new Date()).toISOString(),
  };
}

/**
 * `reason` is declared on BOTH arms rather than only the failure arm.
 *
 * This project compiles with `strictNullChecks: false`, and under that setting
 * a boolean-literal discriminant does not reliably narrow a union — so
 * `if (!outcome.ok) { outcome.reason }` failed to compile even though the check
 * is correct. Declaring the property on both arms keeps the call site honest
 * and readable instead of pushing a cast into it.
 */
export type AuditRecordOutcome =
  | { ok: true; runId: string; reason?: undefined }
  | { ok: false; reason: string; runId?: undefined };

/** Minimal shape of the client needed here, so tests need no live Supabase. */
export interface RpcClient {
  rpc(fn: string, args: Record<string, unknown>): Promise<{ data: unknown; error: unknown }>;
}

/**
 * Record a completed audit.
 *
 * Returns the outcome rather than throwing, and never reports success it did
 * not get. A caller that ignores the result turns this into telemetry instead
 * of a control — the failure mode migration 20260919120000 recorded from a
 * proven case — so the one caller checks it and leaves a breadcrumb.
 *
 * A failure here means no run row, which the read side reports as "not
 * audited". That is the intended fail-safe: the shop is told less, never told
 * something reassuring that nobody verified.
 */
export async function recordIntegrityAudit(
  client: RpcClient,
  input: AuditRecordInput,
): Promise<AuditRecordOutcome> {
  try {
    const { data, error } = await client.rpc(
      'record_integrity_audit',
      buildAuditRecordPayload(input) as unknown as Record<string, unknown>,
    );
    if (error) {
      const message = (error as { message?: string })?.message;
      return { ok: false, reason: message || 'rpc_error' };
    }
    if (typeof data !== 'string' || !data) return { ok: false, reason: 'no_run_id_returned' };
    return { ok: true, runId: data };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : 'unexpected_error' };
  }
}

/* ═════════════════════════ what the checking catches ══════════════════════ */

/**
 * CORE-BQ Slice C (Definition of Done item 9) — the adoption path.
 *
 * A shop is entitled to know what integrity checking looks for BEFORE it has
 * any findings to look at, and "28 named invariants" is not an answer anyone
 * can act on. These are the same rules grouped into the contradictions a person
 * would recognise on their own floor.
 *
 * Every registered rule appears in exactly one family, and a test asserts it.
 * That is the point of building the explanation from the registry rather than
 * writing prose beside it: a rule added later cannot quietly go unexplained,
 * and a rule removed cannot leave a promise behind. The explanation and the
 * engine cannot drift, because the drift fails a test.
 */
export interface IntegrityCheckFamily {
  title: string;
  /** One plain sentence. What a person would call this going wrong. */
  description: string;
  rules: readonly string[];
}

export const INTEGRITY_CHECK_FAMILIES: readonly IntegrityCheckFamily[] = [
  {
    title: 'Two things in one place at once',
    description:
      'A bay holding two vehicles at the same time, or one technician booked into two jobs that overlap.',
    rules: ['NO_OVERLAPS', 'BAY_GLOBAL_OVERLAP', 'SPECIALTY_BAY_DOUBLE_BOOKING',
      'SPECIALTY_BOOKING_OVERLAP'],
  },
  {
    title: 'Work scheduled in the past',
    description:
      'A job still sitting on the board with a start time that has already gone by.',
    rules: ['NO_PAST_TIMES'],
  },
  {
    title: 'Promises that cannot be kept',
    description:
      'A waiter promised their car after their technician has gone home, or specialty work scheduled outside the shift meant to do it.',
    rules: ['WAITER_EOD', 'SPECIALTY_TECH_SHIFT'],
  },
  {
    title: 'Vehicles and bays that disagree',
    description:
      'A parked car still holding a technician, a lift job in a flat bay, specialty work in a bay that cannot do it, or bay assignments the two records describe differently.',
    rules: ['PARKING_LOT_IMMUNITY', 'BAY_TYPE_REQUIREMENT', 'BAY_SYNC', 'SPECIALTY_BAY_TYPE'],
  },
  {
    title: 'Work pointing at something that is not there',
    description:
      'A specialty task with no bay booking, or one referring to a ticket that no longer exists.',
    rules: ['SPECIALTY_TASK_BOOKING', 'SPECIALTY_SUB_REFERENCE', 'SPECIALTY_PRIMARY_REFERENCE'],
  },
  {
    title: 'The wrong person for the job',
    description:
      'Alignment or ADAS work assigned to a technician below the proficiency your shop requires for it.',
    rules: ['SPECIALTY_TECH_PROFICIENCY'],
  },
  {
    title: 'Records in a state they cannot be in',
    description:
      'A finished ticket still holding a bay, a paused job whose timer is running, a completed task with no time recorded — states the shop floor cannot actually be in.',
    rules: ['LIFECYCLE_STATUS_ENUM', 'LIFECYCLE_TIMER_ENUM', 'LIFECYCLE_PAUSED_TIMER',
      'LIFECYCLE_PAUSED_REASON', 'LIFECYCLE_ACTIVE_STALE_PAUSE', 'LIFECYCLE_COMPLETED_TIMER',
      'LIFECYCLE_COMPLETED_BAY', 'LIFECYCLE_TERMINAL_BAY', 'LIFECYCLE_TERMINAL_TIMER',
      'LIFECYCLE_PICKED_UP_LOCATION', 'LIFECYCLE_RUNNING_STATUS',
      'SPECIALTY_STATUS', 'SPECIALTY_ACTUAL_MINUTES'],
  },
] as const;

/**
 * Which of these checks the database REFUSES outright rather than merely
 * reporting. Worth telling a shop apart: a prevented contradiction can never
 * appear on the board at all, whoever or whatever tried to write it.
 */
export function preventedRuleCount(): number {
  return INVARIANT_RULE_IDS.filter((r) => enforcementOf(r).layer === 'database').length;
}
